import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { palette, serifItalic } from './palette';
import { useReveal } from './use-reveal';

export const Section = styled.section<{ $alt?: boolean }>`
  padding: 100px 32px;
  position: relative;
  background: ${({ $alt }) => ($alt ? palette.bgDeep : `transparent`)};

  ${({ theme }) => theme.breakpoints.down(`xs`)} {
    padding: 64px 20px;
  }
`;

export const Container = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

export const Reveal = styled.div<{ $in: boolean }>`
  opacity: ${({ $in: inView }) => (inView ? 1 : 0)};
  transform: ${({ $in: inView }) => (inView ? `none` : `translateY(24px)`)};
  transition: opacity 0.8s ease, transform 0.8s ease;

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    transition: none;
  }
`;

const headlinePartStyles = css<{ $in: boolean }>`
  display: block;
  opacity: ${({ $in: inView }) => (inView ? 1 : 0)};
  transform: ${({ $in: inView }) => (inView ? `none` : `translateY(14px)`)};
  filter: ${({ $in: inView }) => (inView ? `blur(0)` : `blur(12px)`)};
  transition: opacity 0.9s ease, transform 0.9s cubic-bezier(0.2, 0.8, 0.2, 1), filter 0.9s ease;
  will-change: opacity, transform, filter;

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    filter: none;
    transition: none;
  }
`;

const Head = styled.div<{ $in: boolean }>`
  text-align: center;
  margin-bottom: 56px;
  position: relative;

  h2 {
    font-size: clamp(28px, 4vw, 48px);
    font-weight: 700;
    color: ${palette.cream};
    margin: 0;
    line-height: 1.05;
  }

  h2 em {
    ${headlinePartStyles}
    color: ${palette.goldFirst};
    font-style: italic;
    font-weight: 700;
    font-family: ${serifItalic};
  }

  h2 span {
    ${headlinePartStyles}
    transition-delay: 0.14s;
  }

  p {
    margin: 22px auto 0;
    max-width: 640px;
    color: ${palette.creamDim};
    font-size: 16px;
  }
`;

type SectionHeadProps = {
  first: string;
  rest?: string;
  sub?: string;
};

export function SectionHead({ first, rest, sub }: SectionHeadProps): JSX.Element {
  const { ref, inView } = useReveal<HTMLDivElement>();

  return (
    <Head ref={ref} $in={inView}>
      <h2>
        <em>{first}</em>
        {rest && <span>{rest}</span>}
      </h2>
      {sub && <p>{sub}</p>}
    </Head>
  );
}

type RevealOnScrollProps = {
  children: ReactNode;
  className?: string;
};

export function RevealOnScroll({ children, className }: RevealOnScrollProps): JSX.Element {
  const { ref, inView } = useReveal<HTMLDivElement>();

  return (
    <Reveal ref={ref} $in={inView} className={className}>
      {children}
    </Reveal>
  );
}

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  border: 1.5px solid transparent;
  transition: transform 0.08s, background 0.15s;

  &:active {
    transform: translateY(1px);
  }
`;

export const GoldLink = styled.a`
  ${buttonBase}
  background: ${palette.gold};
  color: ${palette.textOnGold};
  border-color: ${palette.gold};

  &:hover {
    background: ${palette.goldBright};
  }
`;

export const GoldButton = styled.button`
  ${buttonBase}
  background: ${palette.gold};
  color: ${palette.textOnGold};
  border-color: ${palette.gold};

  &:hover {
    background: ${palette.goldBright};
  }
`;

export const OutlineLink = styled.a`
  ${buttonBase}
  border-color: ${palette.gold};
  color: ${palette.gold};
  background: transparent;

  &:hover {
    background: rgba(233, 185, 73, 0.08);
  }
`;

export const PillTag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
  font-size: 13px;
  border-radius: 999px;
  border: 1px solid ${palette.cream};
  color: ${palette.cream};
  background: transparent;
`;

export const PillLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
  font-size: 13px;
  border-radius: 999px;
  border: 1px solid ${palette.cream};
  color: ${palette.cream};
  background: transparent;
  text-decoration: none;
  white-space: nowrap;
`;

export const DrawLink = styled.a`
  position: relative;
  padding-bottom: 2px;
  color: ${palette.gold};
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 2px;
    background: ${palette.goldFirst};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.45s cubic-bezier(0.2, 0.8, 0.2, 1);
  }

  &:hover::after {
    transform: scaleX(1);
  }

  .arr {
    display: inline-block;
    transition: transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1);
  }

  &:hover .arr {
    transform: translateX(6px);
  }
`;
